import { gql, useMutation } from '@apollo/client';
import * as React from 'react';
import { useDispatch } from 'react-redux';

import {
  CurrentSelection,
  SelectionTypes,
} from 'modules/campaignIndex/campaignFolders/campaignFolderReducer';
import { CampaignId } from 'modules/campaignIndex/domainTypes';
import { FolderId } from 'modules/domains/foldering';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { NamedFolder, ResponseWrapper } from 'ui/toast';
import noop from 'utils/noop';

const { useEffect } = React;

enum KnownDeleteFailures {
  CannotDeleteRootFolderFailure = 'CannotDeleteRootFolderFailure',
  FolderDoesNotExistFailure = 'FolderDoesNotExistFailure',
}

type DeleteError = {
  id: FolderId | CampaignId;
  name?: string;
  __typename: KnownDeleteFailures;
};

enum DeleteSuccessTypenames {
  folder = 'FolderMutationSuccess',
  content = 'ContentItemSuccess',
}

type DeleteFolderSuccess = {
  id: FolderId;
  name: string;
  __typename: DeleteSuccessTypenames.folder;
};

type DeleteContentSuccess = {
  id: CampaignId;
  name: string;
  __typename: DeleteSuccessTypenames.content;
};

type DeleteCampaignsAndFoldersResponseData = {
  deleteCampaignContentsAndFolders: {
    folderFailures: DeleteError[];
    deletedContent: (DeleteFolderSuccess | DeleteContentSuccess)[];
  };
};

type Variables = {
  contentIds: CampaignId[];
  folderIds: FolderId[];
};

const DeleteCampaignsAndFoldersMutation = gql`
  mutation DeleteCampaignsAndFolders($contentIds: [Long!]!, $folderIds: [Long!]!) {
    deleteCampaignContentsAndFolders(contentIds: $contentIds, folderIds: $folderIds) {
      deletedContent {
        id
        name
      }
      folderFailures {
        ... on ContentsAndFoldersFailure {
          __typename
        }
      }
    }
  }
`;

type Config = {
  onComplete: () => void;
  onError: () => void;
  onSuccess: () => void;
  itemsById: Record<FolderId | CampaignId, CurrentSelection>;
};

export function useDeleteCampaignsAndFolders({
  onComplete = noop,
  onError = noop,
  onSuccess = noop,
  itemsById = {},
}: Config) {
  const dispatch = useDispatch();

  const [deleteCampaignContentsAndFolders, data] = useMutation<
    DeleteCampaignsAndFoldersResponseData,
    Variables
  >(DeleteCampaignsAndFoldersMutation);

  useEffect(() => {
    // don't do anything until the mutation gets called
    if (!data.called) {
      return;
    }

    if (!data.loading && data.error) {
      const errorMessage = 'An unknown error occurred';
      dispatch(
        addNegativeToastWithErrorId(errorMessage, 'useDeleteCampaignsAndFolders', data?.error),
      );
    }

    if (!data.loading && data?.data?.deleteCampaignContentsAndFolders) {
      const { folderFailures, deletedContent } = data.data.deleteCampaignContentsAndFolders;

      folderFailures.forEach((error: DeleteError) => {
        switch (error.__typename) {
          case KnownDeleteFailures.CannotDeleteRootFolderFailure: {
            const errorMessage = <ResponseWrapper>Cannot delete root folder.</ResponseWrapper>;
            dispatch(
              addNegativeToastWithErrorId(errorMessage, 'useDeleteCampaignsAndFolders', error),
            );
            break;
          }

          case KnownDeleteFailures.FolderDoesNotExistFailure: {
            const name = itemsById?.[error?.id]?.name;

            const errorMessage = (
              <ResponseWrapper>
                <NamedFolder name={name} /> folder can not be deleted. It does not exist.
              </ResponseWrapper>
            );

            dispatch(
              addNegativeToastWithErrorId(errorMessage, 'useDeleteCampaignsAndFolders', error),
            );
            break;
          }

          default: {
            const name = itemsById?.[error?.id]?.name || 'Unspecified';
            const errorMessage = (
              <ResponseWrapper>
                <NamedFolder name={name} /> folder could not be deleted.
              </ResponseWrapper>
            );

            dispatch(
              addNegativeToastWithErrorId(errorMessage, 'useDeleteCampaignsAndFolders', error),
            );
          }
        }
        onError();
      });

      deletedContent.forEach((content: DeleteFolderSuccess | DeleteContentSuccess) => {
        const contentType = itemsById[content.id]?.type;
        switch (contentType) {
          case SelectionTypes.Folder: {
            dispatch(
              addPositiveToast(
                <ResponseWrapper>
                  <NamedFolder name={content.name} /> was successfully deleted.
                </ResponseWrapper>,
              ),
            );
            break;
          }

          default: {
            /* Campaign confirmations should be shown as default because itemsById WILL NOT include any
             * nested folders/campaigns that may be deleted/archived. However, the response WILL return
             * 'deleted' (actually archived) nested campaigns { id, name }, but not any nested folders */
            dispatch(
              addPositiveToast(
                <ResponseWrapper>
                  <strong>Campaign {content.name}</strong> was successfully archived.
                </ResponseWrapper>,
              ),
            );
            break;
          }
        }

        onSuccess();
      });

      // call the complete callback
      onComplete();
    }
  }, [data.loading]);

  return [deleteCampaignContentsAndFolders, data] as const;
}
